/**
 * StrategyDraftDataUsagePanel — right-rail summary for the strategy review
 * screen.
 *
 * Shows:
 *   - Total audiences selected (strategy.primaryAudienceBriefRefs.length)
 *   - Theme distribution across sprints
 *   - Aggregate dataUsage citation graph
 *   - Aggregate gap count (N gaps across M sprints)
 *   - audienceCoveragePolicy toggle (advisory ↔ strict)
 *
 * Stateless aside from the policy toggle's pending state; reads strategy + sprints from props.
 */

import React, { useMemo, useState } from 'react';
import {
  Paper,
  Stack,
  Typography,
  Chip,
  Box,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  Tooltip,
} from '@mui/material';
import type {
  ContentStrategy,
  ContentSprint,
  ContentTopic,
  ContentTheme,
  AudienceBriefRef,
} from '@modules/content-plan/types';
import { checkSprintAudienceSufficiency } from '@modules/content-plan/utils/sprintAudienceSufficiency';
import { resolveThemeId } from '@modules/content-plan/utils/themeKeys';

export interface StrategyDraftDataUsagePanelProps {
  strategy: ContentStrategy;
  sprints: ContentSprint[];
  backlog: ContentTopic[];
  themeCatalog: Record<string, ContentTheme>;
  /** Called when the user flips audience coverage policy. */
  onChangePolicy: (next: 'advisory' | 'strict') => Promise<void>;
  disabled?: boolean;
}

export const StrategyDraftDataUsagePanel: React.FC<StrategyDraftDataUsagePanelProps> = ({
  strategy,
  sprints,
  backlog,
  themeCatalog,
  onChangePolicy,
  disabled = false,
}) => {
  const selectedRefs = ((strategy as any).primaryAudienceBriefRefs as
    | AudienceBriefRef[]
    | undefined) ?? [];
  const policy = ((strategy as any).audienceCoveragePolicy as 'advisory' | 'strict' | undefined)
    ?? 'advisory';

  const [policyPending, setPolicyPending] = useState<'advisory' | 'strict' | null>(null);
  const [policyError, setPolicyError] = useState<string | null>(null);

  const sufficiency = useMemo(() => {
    if (sprints.length === 0) return null;
    return checkSprintAudienceSufficiency({
      sprints,
      backlog,
      selectedAudienceBriefRefs: selectedRefs,
      themeCatalog,
    });
  }, [sprints, backlog, selectedRefs, themeCatalog]);

  const sprintsWithGaps = sufficiency?.perSprint.filter((s) => !s.isSufficient).length ?? 0;
  const totalGap = sufficiency?.estimatedTopicsToGenerate ?? 0;

  // Theme distribution across sprints
  const themeDistribution = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const sprint of sprints) {
      const quotas = ((sprint as any).sprintContract?.outputThemeQuotas as
        | Array<{ themeId?: string; theme?: string; requiredCount: number }>
        | undefined) ?? [];
      for (const q of quotas) {
        const themeId = resolveThemeId(q);
        if (!themeId) continue;
        counts[themeId] = (counts[themeId] ?? 0) + q.requiredCount;
      }
    }
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);
  }, [sprints]);

  // Aggregate dataUsage paths across the strategy + every sprint
  const citationCount = useMemo(() => {
    const set = new Set<string>();
    const stratUsage = ((strategy as any).dataUsage?.items as
      | Array<{ sourcePath?: string }>
      | undefined) ?? [];
    stratUsage.forEach((it) => it.sourcePath && set.add(it.sourcePath));
    for (const sprint of sprints) {
      const items = ((sprint as any).sprintDataUsage?.items as
        | Array<{ sourcePath?: string }>
        | undefined) ?? [];
      items.forEach((it) => it.sourcePath && set.add(it.sourcePath));
    }
    return set.size;
  }, [strategy, sprints]);

  const handleChangePolicy = async (_: unknown, next: 'advisory' | 'strict' | null) => {
    if (!next || next === policy) return;
    setPolicyError(null);
    setPolicyPending(next);
    try {
      await onChangePolicy(next);
    } catch (e) {
      setPolicyError((e as Error).message);
    } finally {
      setPolicyPending(null);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, position: 'sticky', top: 16 }} data-testid="strategy-draft-data-usage-panel">
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
        Strategy summary
      </Typography>

      {/* Audiences */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Audiences selected
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {selectedRefs.length === 0 ? (
            <Chip size="small" variant="outlined" label="0" color="warning" />
          ) : (
            selectedRefs.map((r) => (
              <Tooltip key={`${r.analysisId}-${r.audienceId}`} title={r.sourcePath}>
                <Chip
                  size="small"
                  color="primary"
                  label={r.labelSnapshot ?? r.audienceId}
                />
              </Tooltip>
            ))
          )}
        </Stack>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* Sprint gap aggregate */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Sufficiency
        </Typography>
        {sufficiency?.allSufficient ? (
          <Alert severity="success" sx={{ p: 0.5, '.MuiAlert-message': { fontSize: 12 } }}>
            Backlog covers every sprint × audience × theme.
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ p: 0.5, '.MuiAlert-message': { fontSize: 12 } }}>
            {`${totalGap} target unit${totalGap === 1 ? '' : 's'} uncovered across ${sprintsWithGaps} sprint${sprintsWithGaps === 1 ? '' : 's'}.`}
          </Alert>
        )}
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* Theme distribution */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Top themes (across sprints)
        </Typography>
        {themeDistribution.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No theme quotas yet.
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {themeDistribution.map(([themeId, count]) => (
              <Stack key={themeId} direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Typography variant="caption" sx={{ flex: 1, minWidth: 0 }} noWrap>
                  {themeCatalog[themeId]?.name ?? themeId}
                </Typography>
                <Chip size="small" variant="outlined" label={count} />
              </Stack>
            ))}
          </Stack>
        )}
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* Citations */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Data citations
        </Typography>
        <Chip size="small" variant="outlined" label={`${citationCount} unique source${citationCount === 1 ? '' : 's'}`} />
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* Coverage policy */}
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Coverage policy
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={policy}
          onChange={handleChangePolicy}
          disabled={disabled || policyPending !== null}
          aria-label="audienceCoveragePolicy"
        >
          <ToggleButton value="advisory" data-testid="coverage-policy-advisory">
            advisory
          </ToggleButton>
          <ToggleButton value="strict" data-testid="coverage-policy-strict">
            strict
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {policy === 'strict'
            ? 'Approve fails (422) if gaps remain after planning. You resolve and retry.'
            : 'Approve always proceeds; gaps surface as warnings on each sprint.'}
        </Typography>
        {policyError && (
          <Alert severity="error" sx={{ mt: 1, p: 0.5, '.MuiAlert-message': { fontSize: 12 } }}>
            {policyError}
          </Alert>
        )}
      </Box>
    </Paper>
  );
};

export default StrategyDraftDataUsagePanel;
